"use client"

import { useEffect, useState } from "react"
import dynamic from "next/dynamic"

// GuideModal pulls in framer-motion + lucide icons. It only ever renders on
// an explicit user interaction (#guide hash or the `openGuide` event), so keep
// it out of the initial homepage JS and load it lazily on first open.
const GuideModal = dynamic(() => import("@/components/guide-modal"), { ssr: false })

/**
 * Thin client island for the homepage's only interactive concerns:
 * hash-based scroll navigation and the install-guide modal. Extracting this
 * lets the rest of the homepage render as Server Components (independent
 * hydration islands instead of one monolithic client tree) — the primary
 * TBT / simulated-LCP lever for TES-833.
 */
export default function HomeInteractions() {
  const [isGuideOpen, setIsGuideOpen] = useState(false)
  // Once the modal has been opened we keep GuideModal mounted so its
  // AnimatePresence exit animation still plays on close.
  // eslint-disable-next-line react-doctor/no-initialize-state -- hasOpenedGuide is a one-way latch (false→true); correct pattern to keep GuideModal mounted for exit animation
  const [hasOpenedGuide, setHasOpenedGuide] = useState(false)

  const openGuide = () => {
    setHasOpenedGuide(true)
    setIsGuideOpen(true)
  }

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1)
      if (!hash) return
      if (hash === "guide") {
        openGuide()
      } else {
        document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" })
      }
    }

    const handleOpenGuide = () => openGuide()

    handleHashChange()

    window.addEventListener("hashchange", handleHashChange)
    window.addEventListener("openGuide", handleOpenGuide)

    return () => {
      window.removeEventListener("hashchange", handleHashChange)
      window.removeEventListener("openGuide", handleOpenGuide)
    }
  }, [])

  if (!hasOpenedGuide) return null

  return <GuideModal isOpen={isGuideOpen} onClose={() => setIsGuideOpen(false)} />
}
